import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { getSignedFileUrl } from '../../services/api';

/**
 * Secure file download component that generates pre-signed URLs
 * 
 * @param {Object} props - Component props
 * @param {Object} props.file - File object with key or url
 * @param {string} props.tooltip - Tooltip text (default: "Download")
 * @param {Object} props.iconButtonProps - Additional props for IconButton
 * @param {Function} props.onError - Error callback
 */
const SecureFileDownload = ({ 
  file, 
  tooltip = "Download",
  iconButtonProps = {},
  onError
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!file) {
      console.error('No file provided for download');
      if (onError) onError(new Error('No file provided'));
      return;
    }

    setIsLoading(true);
    
    try {
      // Extract the file key - handle different file object structures
      let fileKey = null;
      
      if (typeof file === 'string') {
        fileKey = file;
      } else if (file.key) {
        fileKey = file.key;
      } else if (file.url) {
        // Extract key from URL if possible
        const urlParts = file.url.split('/');
        const possibleKey = urlParts[urlParts.length - 1];
        if (possibleKey && !possibleKey.includes('http')) {
          fileKey = possibleKey;
        }
      }
      
      if (!fileKey) {
        console.error('No file key found in:', file);
        if (onError) onError(new Error('No file key found'));
        return;
      }

      // Get pre-signed URL
      const signedUrl = await getSignedFileUrl(fileKey);
      
      if (signedUrl) {
        // Open in new tab for download
        window.open(signedUrl, '_blank');
      } else {
        throw new Error('Failed to generate download URL');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      
      // Provide more user-friendly error messages
      let userMessage = 'Download failed';
      if (error.message?.includes('File not found') || error.message?.includes('404')) {
        userMessage = 'This file is no longer available or may have been moved';
      } else if (error.message?.includes('network') || error.message?.includes('Network')) {
        userMessage = 'Network error - please check your connection';
      } else if (error.message) {
        userMessage = error.message;
      }
      
      if (onError) onError(new Error(userMessage));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Tooltip title={tooltip}>
      <IconButton
        edge="end"
        onClick={handleDownload}
        disabled={isLoading}
        {...iconButtonProps}
      >
        {isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <DownloadIcon />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default SecureFileDownload;